import React, { useState, useEffect } from 'react';
import {
  Card,
  Stack,
  Thumbnail,
  Button,
  Modal,
  TextContainer,
  Text,
  Banner,
  Heading,
  Portal
} from '@shopify/polaris';

/**
 * Component for selecting product images for Image Analyzing templates
 */
export const ImageSelector = ({
  product,
  selectedImage, // For backward compatibility - single image
  selectedImages, // New prop for multiple images
  onSelectImage,
  onSelectImages, // New callback for multiple images
  maxImages = 1
}) => {
  const [showModal, setShowModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [internalSelectedImages, setInternalSelectedImages] = useState([]);

  // Initialize selected images based on props
  useEffect(() => {
    if (maxImages > 1) {
      // Multiple images mode
      setInternalSelectedImages(selectedImages || []);
    } else {
      // Single image mode (backward compatibility)
      setInternalSelectedImages(selectedImage ? [selectedImage] : []);
    }
  }, [selectedImage, selectedImages, maxImages]);

  // Extract images from product when it changes
  useEffect(() => {
    if (product && product.images) {
      // Transform product images into a consistent format
      const images = product.images.map(image => ({
        id: image.id,
        url: image.src || image.url,
        alt: image.alt || '',
        position: image.position || 0,
        name: image.alt || `Image ${image.position || 0}`
      }));
      
      // Sort by position
      images.sort((a, b) => a.position - b.position);
      
      setProductImages(images);
    } else {
      setProductImages([]);
    }
  }, [product]);

  // Open image preview modal
  const handlePreview = (image) => {
    setPreviewImage(image);
    setShowModal(true);
  };

  // Select an image
  const handleSelect = (image) => {
    if (maxImages > 1) {
      // Multiple images mode
      const newSelectedImages = [...internalSelectedImages];
      const index = newSelectedImages.findIndex(img => img.id === image.id);
      if (index >= 0) {
        newSelectedImages.splice(index, 1);
      } else if (newSelectedImages.length < maxImages) {
        newSelectedImages.push(image);
      }
      setInternalSelectedImages(newSelectedImages);
      if (onSelectImages) {
        onSelectImages(newSelectedImages);
      }
    } else {
      // Single image mode (backward compatibility)
      setInternalSelectedImages([image]);
      if (onSelectImage) {
        onSelectImage(image);
      }
    }
  };

  return (
    <>
      <Card sectioned>
        <Stack vertical spacing="tight">
          <Heading>Select Product Image{maxImages > 1 ? 's' : ''}</Heading>
          <Text variant="bodyMd" as="p">
            Select an image{maxImages > 1 ? 's' : ''} to analyze with the AI model. The selected image{maxImages > 1 ? 's' : ''} will be processed using the vision-capable AI model.
            {maxImages > 1 && (
              <span style={{ display: 'block', marginTop: '8px', fontWeight: 'bold', color: '#008060' }}>
                {internalSelectedImages.length} of {maxImages} images selected
              </span>
            )}
          </Text>

          {productImages.length === 0 ? (
            <Banner status="warning">
              <p>No images available for this product.</p>
            </Banner>
          ) : (
            <Stack wrap={true}>
              {productImages.map((image) => (
                <Stack.Item key={image.id}>
                  <div 
                    style={{
                      padding: '10px',
                      border: internalSelectedImages.find(img => img.id === image.id) ? '2px solid #008060' : '1px solid #ddd',
                      borderRadius: '4px',
                      margin: '5px',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                  >
                    <Stack vertical spacing="tight" alignment="center">
                      <Thumbnail
                        source={image.url}
                        alt={image.alt || 'Product image'}
                        size="large"
                        onClick={() => handlePreview(image)}
                      />
                      <Stack spacing="tight">
                        <Button size="slim" onClick={() => handlePreview(image)}>
                          Preview
                        </Button>
                        <Button 
                          primary={!internalSelectedImages.find(img => img.id === image.id)}
                          size="slim" 
                          onClick={() => handleSelect(image)}
                        >
                          {internalSelectedImages.find(img => img.id === image.id) ? 'Selected' : 'Select'}
                        </Button>
                      </Stack>
                    </Stack>
                    {internalSelectedImages.find(img => img.id === image.id) && (
                      <div style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        backgroundColor: '#008060',
                        color: 'white',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                      }}>
                        ✓
                      </div>
                    )}
                  </div>
                </Stack.Item>
              ))}
            </Stack>
          )}
        </Stack>
      </Card>

      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '90%',
            maxHeight: '90%',
            overflow: 'auto',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
              <h2>Image Preview</h2>
              <button 
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}
              >
                ×
              </button>
            </div>
            {previewImage && (
              <div style={{ textAlign: 'center' }}>
                <img 
                  src={previewImage.url} 
                  alt={previewImage.alt || 'Product image'} 
                  style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain' }} 
                />
                <p>{previewImage.alt || 'Product image'}</p>
              </div>
            )}
            
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f6f6f7',
                  border: '1px solid #c4cdd5',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              
              <button
                onClick={() => {
                  handleSelect(previewImage);
                  setShowModal(false);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#008060',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Select This Image
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageSelector;
